import { useMemo } from "react";
import { Preview } from "views/pages/DataPage/CardsView/DataList";
import { FilterTab } from "views/pages/DataPage/CardsView/ReviewList";

interface ReviewCounters {
  total: number;
  forReview: number;
}

interface UseReviewCountersParams {
  selectedReview: any;
  labelId: string | null;
  selectedFilterTab: FilterTab;
  totalReviewsCount?: number;
  pendingReviewsCount?: number;
  loading: boolean;
}

/**
 * Custom hook to calculate review counters based on current context
 * Centralizes counter logic to avoid duplication
 */
export function useReviewCounters({
  selectedReview,
  labelId,
  selectedFilterTab,
  totalReviewsCount,
  pendingReviewsCount,
  loading,
}: UseReviewCountersParams): ReviewCounters | null {
  return useMemo(() => {
    if (loading) {
      return null;
    }

    let total = 0;
    let pending = 0;

    if (selectedReview && labelId) {
      const previews = selectedReview.previews;

      // Map each tab to its filtered previews
      const filteredPreviewsMap: Record<FilterTab, Preview[]> = {
        All: previews,
        "For Review": previews.filter((p: Preview) => !p?.completed),
        Annotated: previews.filter((p: Preview) => p?.completed),
      };

      const filteredPreviews = filteredPreviewsMap[selectedFilterTab || "All"];

      total = filteredPreviews.length;
      if (selectedFilterTab === "All") {
        pending = filteredPreviews.filter((p) => !p.completed).length;
      }
    } else {
      total = totalReviewsCount || 0;
      pending = pendingReviewsCount || 0;
    }

    return {
      total,
      forReview: pending,
    };
  }, [
    loading,
    selectedReview,
    labelId,
    selectedFilterTab,
    totalReviewsCount,
    pendingReviewsCount,
  ]);
}
